import { v4 as uuid } from 'uuid';
import { RDF_TYPE_IRI } from 'vet-bones/constants';

import { ProjectPropertyType } from 'src/constants/project';
import {
  ProjectModel,
  ProjectModelClass,
  ProjectModelProperty,
} from 'src/store/state/projects';
import {
  OWL_CLASS_IRI,
  OWL_DATATYPE_PROPERTY_IRI,
  OWL_OBJECT_PROPERTY_IRI,
  RDFS_CLASS_IRI,
  RDFS_COMMENT_IRI,
  RDFS_DOMAIN_IRI,
  RDFS_LABEL_IRI,
  RDFS_RANGE_IRI,
  RDFS_SUB_CLASS_OF_IRI,
  RDFS_SUB_PROPERTY_OF_IRI,
  RDF_PROPERTY_IRI,
  SCHEMA_ORG_DOMAIN_INCLUDES_IRI,
  SCHEMA_ORG_HTTP_DOMAIN_INCLUDES_IRI,
  SCHEMA_ORG_HTTP_RANGE_INCLUDES_IRI,
  SCHEMA_ORG_HTTP_SUPERSEDED_BY_IRI,
  SCHEMA_ORG_RANGE_INCLUDES_IRI,
  SCHEMA_ORG_SUPERSEDED_BY_IRI,
} from 'src/templates/queries/iris';
import { generateDateRandomId } from 'src/utils/generateId';
import {
  Triple,
  parseTurtleTriples,
} from 'src/utils/voicebox/parsing/parseTurtleTriples';

export const createVoiceboxModel = (turtle: string): ProjectModel | null => {
  const parsed = parseTurtleTriples(turtle);
  if (parsed.errors.length || parsed.semanticErrors.length) {
    console.warn({
      errors: parsed.errors,
      semanticErrors: parsed.semanticErrors,
    });
  }

  const classes: Triple[] = [];

  const datatypeProperties: Triple[] = [];
  const objectProperties: Triple[] = [];

  const domains: Triple[] = [];
  const domainIncludes: Triple[] = [];
  const ranges: Triple[] = [];
  const rangeIncludes: Triple[] = [];

  const subClass: Triple[] = [];
  const subPropertyOf: Triple[] = [];

  const comments: Triple[] = [];
  const labels: Triple[] = [];

  // categorize each triple
  parsed.triples.forEach((triple) => {
    if (
      triple.predicate === RDF_TYPE_IRI &&
      (triple.object === RDFS_CLASS_IRI || triple.object === OWL_CLASS_IRI)
    ) {
      classes.push(triple);
    }

    if (
      triple.predicate === RDF_TYPE_IRI &&
      triple.object === OWL_DATATYPE_PROPERTY_IRI
    ) {
      datatypeProperties.push(triple);
    }
    if (
      triple.predicate === RDF_TYPE_IRI &&
      (triple.object === RDF_PROPERTY_IRI ||
        triple.object === OWL_OBJECT_PROPERTY_IRI)
    ) {
      objectProperties.push(triple);
    }

    if (triple.predicate === RDFS_DOMAIN_IRI) {
      domains.push(triple);
    }
    if (
      triple.predicate === SCHEMA_ORG_DOMAIN_INCLUDES_IRI ||
      triple.predicate === SCHEMA_ORG_HTTP_DOMAIN_INCLUDES_IRI
    ) {
      domainIncludes.push(triple);
    }
    if (triple.predicate === RDFS_RANGE_IRI) {
      ranges.push(triple);
    }
    if (
      triple.predicate === SCHEMA_ORG_RANGE_INCLUDES_IRI ||
      triple.predicate === SCHEMA_ORG_HTTP_RANGE_INCLUDES_IRI
    ) {
      rangeIncludes.push(triple);
    }

    if (triple.predicate === RDFS_SUB_CLASS_OF_IRI) {
      subClass.push(triple);
    }
    if (
      triple.predicate === RDFS_SUB_PROPERTY_OF_IRI ||
      triple.predicate === SCHEMA_ORG_SUPERSEDED_BY_IRI ||
      triple.predicate === SCHEMA_ORG_HTTP_SUPERSEDED_BY_IRI
    ) {
      subPropertyOf.push(triple);
    }

    if (triple.predicate === RDFS_COMMENT_IRI) {
      comments.push(triple);
    }
    if (triple.predicate === RDFS_LABEL_IRI) {
      labels.push(triple);
    }
  });

  const projectClasses: Record<string, ProjectModelClass> = {};
  classes.forEach((singleClass) => {
    const parentClasses = subClass.filter(
      (sub) => sub.subject === singleClass.subject
    );
    const parents = [...parentClasses.map((parent) => parent.object)];

    projectClasses[singleClass.subject] = {
      iri: singleClass.subject,
      parents,
    };
  });

  const projectProperties: Record<string, ProjectModelProperty> = {};

  [...datatypeProperties, ...objectProperties].forEach((property) => {
    const domain = [...domains, ...domainIncludes].filter(
      (domain) => domain.subject === property.subject
    );
    const range = [...ranges, ...rangeIncludes].filter(
      (range) => range.subject === property.subject
    );
    const parents = subPropertyOf.filter(
      (subProperty) => subProperty.subject === property.subject
    );

    let isDatatype: boolean | undefined;
    if (property.object === OWL_DATATYPE_PROPERTY_IRI) {
      isDatatype = true;
    } else if (property.object === OWL_OBJECT_PROPERTY_IRI) {
      isDatatype = false;
    }

    projectProperties[property.subject] = {
      iri: property.subject,
      domain: domain.map((domain) => domain.object),
      range: range.map((range) => range.object),
      parents: parents.map((parent) => parent.object),
      isDatatype,
      // we always assume SCHEMA_ORG is used (domainIncludes/rangeIncludes)
      // because that is what we ask for in our LLM prompting
      propertyType: ProjectPropertyType.SCHEMA_ORG,
    };
  });

  const descriptionsByIri: Record<string, string> = {};
  comments.forEach((comment) => {
    descriptionsByIri[comment.subject] = comment.object;
  });

  const labelsByIri: Record<string, string> = {};
  labels.forEach((label) => {
    labelsByIri[label.subject] = label.object;
  });

  const databaseNamespaces: Record<string, string> = {};
  Object.entries(parsed.prefixes).forEach((entry) => {
    const [prefix, iri] = entry;
    databaseNamespaces[prefix] = iri;
  });

  if (
    !Object.keys(projectClasses).length &&
    !Object.keys(projectProperties).length
  ) {
    return null;
  }

  return {
    connectionId: '',
    databaseId: '',
    databaseNamespaces,
    id: uuid(),
    name: `VoiceboxModel-${generateDateRandomId()}`,
    dateLastUpdated: Date.now(),
    classes: projectClasses,
    properties: projectProperties,
    rules: {},
    colorIdsByIri: {},
    descriptionsByIri,
    labelsByIri,
    voicebox: true,
    draft: true,
  };
};
